import React from 'react';

import { useGeneralRisk } from '../hooks/useGeneralRisk';

import ActionsByOwnerBars from './ActionsByOwnerBars';
import Card, { Flag } from './DashboardCard';
import { DashboardLoading, fmt } from './dashboardFormatting';
import DepartmentAfcChart from './DepartmentAfcChart';
import MovementOverviewGrid from './MovementOverviewGrid';
import RadioDonut from './RadioDonut';
import RadioTileStrip from './RadioTileStrip';
import { ragDonutSegments, ragTotal } from './ragHelpers';

// IB-677 rework — cross-type Overview leading the module: KPI strip → the compact
// three-widget row (RAG profile donut · movement per category · actions by owner) →
// the full-width department AFC diverging chart.
export default function OverviewDashboard() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const overview = dashboardData?.overview || {};
    const movement = overview.movement;
    const basisLabel = $t('residual');
    const profileCounts = dashboardData?.risks?.count_by_controlled_classification;
    const redCount = overview.red_risks;

    // net = risk weighted value − opportunity weighted value, so it goes negative
    // once opportunities outweigh risks. That net-positive position is presented
    // explicitly (magnitude + relabel + note) rather than as a bare negative.
    const net = Number(overview.net) || 0;
    const netFavoursOpportunities = net < 0;
    const riskMovement = movement?.has_baseline ? `+${movement.risks?.new ?? 0} / −${movement.risks?.closed ?? 0}` : null;

    const tiles = [
        {
            lead: true,
            label: netFavoursOpportunities ? $t('Net opportunity position') : $t('Net risk-weighted value'),
            value: fmt(Math.abs(net)),
            sub: (
                <>
                    <div>
                        {$t('In AFC')} {fmt(overview.net_in_afc)} · {$t('Not in AFC')} {fmt(overview.net_not_in_afc)}
                    </div>
                    {netFavoursOpportunities && (
                        <div className="tw-mt-1">{$t('Opportunities currently outweigh risk exposure — this does not mean risk-free')}</div>
                    )}
                </>
            ),
        },
        { tone: 'red', label: `${$t('Red risks')} (${basisLabel})`, value: redCount ?? 0, sub: riskMovement },
        { label: $t('Decisions pending'), value: overview.decisions_pending ?? 0 },
        { tone: 'warn', label: $t('Overdue actions'), value: overview.overdue_actions ?? 0 },
        { label: $t('Unverified assumptions'), value: overview.unverified_assumptions ?? 0 },
    ];

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <RadioTileStrip tiles={tiles} />

            <div className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-3">
                <Card title={`${$t('RAG profile — open risks')} (${basisLabel})`} minHeight={170}>
                    <RadioDonut segments={ragDonutSegments(profileCounts)} total={`${ragTotal(profileCounts)} ${$t('open')}`} />
                </Card>
                <Card title={$t('Movement this period')} flag={<Flag tone="new">{$t('new')}</Flag>} minHeight={170}>
                    <MovementOverviewGrid movement={movement} />
                </Card>
                <Card title={$t('Actions by owner')} flag={<Flag tone="new">{$t('promoted')}</Flag>} minHeight={170}>
                    <ActionsByOwnerBars dashboardData={dashboardData} />
                </Card>
            </div>

            <Card title={$t('Budget risks & opportunities by department — in / not in AFC')} flag={<Flag tone="new">{$t('new')}</Flag>}>
                <DepartmentAfcChart data={overview.department_diverging} />
            </Card>
        </div>
    );
}
